import React, { useEffect } from 'react';
import SimpleModal from '@bufferapp/ui/SimpleModal';
import { useAppDispatch, useAppSelector } from '~/store';
import { useTranslation } from 'react-i18next';
import { BufferTracker } from '@bufferapp/buffer-tracking-browser-ts';
import { Idea, NewIdea } from '~/content/types';
import {
  addNotification,
  addPermanentNotification,
} from '~/notifications-provider/notificationsSlice';
import { useAccount } from '~/accountContext';
import { SEGMENT_NAMES } from '~/constants';
import { useCreateIdea } from '~/content/components/hooks/useCreateIdea';
import { useEditIdea } from '~/content/components/hooks/useEditIdea';
import { selectIsIdeaFromExtension } from '~/content/state/ideasSlice';
import { useDeleteIdea } from '../../hooks/useDeleteIdea';
import {
  ModalWrapperStyled,
  ModalActionsStyled,
  TitleStyled,
  ParagraphStyled,
  ButtonStyled,
} from './styles';

type Props = {
  idea: Idea | NewIdea;
  composerSuccessMessage: string;
  onDiscardModal;
};

export const PostSuccessModal: React.FC<Props> = ({
  idea,
  composerSuccessMessage,
  onDiscardModal,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const fromExtension = useAppSelector((state) =>
    selectIsIdeaFromExtension(state)
  );
  const { deleteIdea, deleteIdeaData, deleteIdeaError } = useDeleteIdea();

  const { createIdea, createIdeaData, createIdeaError } = useCreateIdea();

  const { updateIdea, updateIdeaData, updateIdeaError } = useEditIdea();

  // Get organizationID for Tracking purposes
  const accountContext = useAccount() as any;
  const account = accountContext?.account;
  const organizationId = account?.currentOrganization?.id;

  useEffect(() => {
    const error =
      updateIdeaError || (updateIdeaData && updateIdeaData.ideaUpdate?.error);
    if (error) {
      dispatch(
        addPermanentNotification(
          `⚠️ ${t('content.notifications.keepIdea.errorMessage')}`
        )
      );
      onDiscardModal();
    } else if (updateIdeaData && updateIdeaData.ideaUpdate.success) {
      dispatch(
        addNotification(
          `✅ ${t('content.notifications.keepIdea.successMessage')}`
        )
      );
      onDiscardModal();
    }
  }, [updateIdeaData]);

  useEffect(() => {
    const error =
      createIdeaError || (createIdeaData && !createIdeaData.ideaCreate.success);
    if (createIdeaData && createIdeaData.ideaCreate.success) {
      dispatch(
        addNotification(
          `✅ ${t('content.notifications.keepIdea.successMessage')}`
        )
      );
      onDiscardModal();
    } else if (error) {
      dispatch(
        addPermanentNotification(
          `⚠️ ${t('content.notifications.keepIdea.errorMessage')}`
        )
      );
      onDiscardModal();
    }
  }, [createIdeaData]);

  useEffect(() => {
    const error =
      deleteIdeaError ||
      (deleteIdeaData && !deleteIdeaData.ideasRemove.success);
    if (deleteIdeaData && deleteIdeaData.ideasRemove.success) {
      dispatch(
        addNotification(
          `✅ ${t('content.notifications.discardIdea.successMessage')}`
        )
      );
      onDiscardModal();
    } else if (error) {
      dispatch(
        addPermanentNotification(
          `⚠️ ${t('content.notifications.discardIdea.errorMessage')}`
        )
      );
      onDiscardModal();
    }
  }, [deleteIdeaData]);

  const handleKeepIdea = () => {
    if (idea.id) {
      updateIdea(idea as Idea);
    } else {
      createIdea(idea.content, { isFromExtension: fromExtension });
    }
    BufferTracker.ideaKept({
      clientName: 'publishWeb',
      organizationId,
      ideaId: idea.id,
      cta: SEGMENT_NAMES.IDEA_KEEP_ON_SAVE_POST,
    });
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleKeepIdea();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const handleDiscardIdea = () => {
    if (idea.id) {
      deleteIdea(idea.id);
    } else {
      dispatch(
        addNotification(
          `✅ ${t('content.notifications.discardIdea.successMessage')}`
        )
      );
      onDiscardModal();
    }
    BufferTracker.ideaDiscarded({
      clientName: 'publishWeb',
      organizationId,
      ideaId: idea.id,
      cta: SEGMENT_NAMES.IDEA_DISCARD_ON_SAVE_POST,
    });
  };

  return (
    <SimpleModal closeAction={onDiscardModal} data-testid="post-success-modal">
      <ModalWrapperStyled>
        <TitleStyled type="h3">{composerSuccessMessage}</TitleStyled>
        <ParagraphStyled type="p">
          {t('content.modals.postSuccess.description')}
        </ParagraphStyled>
        <ModalActionsStyled>
          <ButtonStyled
            type="text"
            onClick={handleDiscardIdea}
            label={t('content.modals.postSuccess.discard')}
          />
          <ButtonStyled
            type="primary"
            onClick={handleKeepIdea}
            label={t('content.modals.postSuccess.keep')}
          />
        </ModalActionsStyled>
      </ModalWrapperStyled>
    </SimpleModal>
  );
};
